/* eslint react-hooks/exhaustive-deps: "warn" */
import { type KeyboardEvent, useRef, useState } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';

import { Button } from 'ui/atoms/Button/Button';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';

import styles from './TechnologyTeams.module.scss';

type Translate = (key: string) => string;

type Team = {
  number: string;
  tab: string;
  idLabel: string;
  title: string;
  pull: string;
  decideBefore: string;
  decideBold: string;
  decideAfter: string;
  ctaLabel: string;
  ctaHref: string;
};

const getTeams = (t: Translate): Team[] => [
  {
    number: '01',
    tab: t('Growth and RevOps'),
    idLabel: t('01 · growth and revops'),
    title: t('Growth and RevOps'),
    pull: t('Firmographics, filed accounts and group structure written onto the account object.'),
    decideBefore: t('Whether territory maths and routing rest on '),
    decideBold: t('filed numbers or typed notes'),
    decideAfter: '.',
    ctaLabel: t('Data enrichment'),
    ctaHref: '/data-enrichment',
  },
  {
    number: '02',
    tab: t('Fintech and payments'),
    idLabel: t('02 · fintech and payments'),
    title: t('Fintech and payments'),
    pull: t('Registry verification, ownership chains and screening, called at signup.'),
    decideBefore: t('Whether a business customer '),
    decideBold: t('clears onboarding without a human'),
    decideAfter: '.',
    ctaLabel: t('KYB verification'),
    ctaHref: '/kyb-verification',
  },
  {
    number: '03',
    tab: t('Marketplaces and platforms'),
    idLabel: t('03 · marketplaces and platforms'),
    title: t('Marketplaces and platforms'),
    pull: t('Merchant verification, plus webhook events on status, officers and ownership.'),
    decideBefore: t('Whether a lapsed seller is caught '),
    decideBold: t('the day the filing lands'),
    decideAfter: t(', or at the next annual review.'),
    ctaLabel: t('Portfolio monitoring'),
    ctaHref: '/portfolio-monitoring',
  },
  {
    number: '04',
    tab: t('Product and engineering'),
    idLabel: t('04 · product and engineering'),
    title: t('Product and engineering'),
    pull: t('One REST endpoint, one schema, open identifiers, one key across every surface.'),
    decideBefore: t('How much of the company graph '),
    decideBold: t('you never have to build'),
    decideAfter: '.',
    ctaLabel: t('API docs'),
    ctaHref: 'https://api.globaldatabase.com/docs/v2',
  },
  {
    number: '05',
    tab: t('Procurement and vendor risk'),
    idLabel: t('05 · procurement and vendor risk'),
    title: t('Procurement and vendor risk'),
    pull: t('Supplier registry records, ownership and sanctions exposure across the whole group.'),
    decideBefore: t('Whether a supplier file '),
    decideBold: t("survives an auditor's question"),
    decideAfter: '.',
    ctaLabel: t('Supply chain risk'),
    ctaHref: '/supply-chain-risk',
  },
  {
    number: '06',
    tab: t('AI and agent teams'),
    idLabel: t('06 · ai and agent teams'),
    title: t('AI and agent teams'),
    pull: t('Grounded retrieval over MCP, with the citation attached to the answer.'),
    decideBefore: t("Whether an agent's answer can be "),
    decideBold: t('defended when a regulator asks'),
    decideAfter: t(' where it came from.'),
    ctaLabel: t('MCP server'),
    ctaHref: '/mcp',
  },
];

export const TechnologyTeams = () => {
  const { t } = useInt();
  const teams = getTeams(t);
  const [active, setActive] = useState(0);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const pick = (index: number) => {
    setActive(index);
    tabRefs.current[index]?.focus();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    let next: number | null = null;
    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
      next = (index + 1) % teams.length;
    }
    if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
      next = (index - 1 + teams.length) % teams.length;
    }
    if (next !== null) {
      event.preventDefault();
      pick(next);
    }
  };

  const team = teams[active];

  return (
    <section className={styles.root}>
      <div className={styles.grid}>
        <div>
          <TitleBlock align="left" extra={t('where it lands')} title={t('Six teams. One dependency.')} />

          <div className={styles.list} role="tablist" aria-label={t('Teams')}>
            {teams.map((item, i) => (
              <button
                type="button"
                role="tab"
                aria-selected={active === i}
                id={`tm-tab-${i}`}
                aria-controls={`tm-pane-${i}`}
                className={styles.tab}
                ref={(el) => {
                  tabRefs.current[i] = el;
                }}
                onClick={() => pick(i)}
                onKeyDown={(event) => onKeyDown(event, i)}
                key={item.tab}
              >
                <span className={styles.n}>{item.number}</span>
                <span className={styles.t}>{item.tab}</span>
                <span className={styles.ar}>&rarr;</span>
              </button>
            ))}
          </div>
        </div>

        <div className={styles.cardWrap}>
          <div
            className={styles.card}
            id={`tm-pane-${active}`}
            role="tabpanel"
            aria-labelledby={`tm-tab-${active}`}
            key={active}
          >
            <span className={styles.ghostN} aria-hidden="true">
              {team.number}
            </span>

            <div className={styles.id}>{team.idLabel}</div>
            <h3 className={styles.cardTitle}>{team.title}</h3>

            <div className={styles.field}>
              <span className={styles.lab}>{t('what they pull')}</span>
              <p>{team.pull}</p>
            </div>

            <div className={cn(styles.field, styles.fieldLast)}>
              <span className={styles.lab}>{t('what it decides')}</span>
              <p>
                {team.decideBefore}
                <b>{team.decideBold}</b>
                {team.decideAfter}
              </p>
            </div>

            <div className={styles.cta}>
              <Button type="text" size="md" href={team.ctaHref} suffix="→">
                {team.ctaLabel}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
